import { useEffect, useRef, useState } from 'react';

const notList = [
  '01  A marketing agency that runs ads',
  '02  A lead generation platform',
  '03  A construction company',
  '04  A software or CRM tool',
  '05  A franchise model',
];

const weAreList = [
  {
    title: 'Your Commercial Department',
    desc: 'We act as your internal sales team —\nattending meetings, closing deals and\nmanaging client relationships.',
  },
  {
    title: 'Your Brand Infrastructure',
    desc: 'Logo, website, social media, showroom\npresence — all created and managed\nunder your name.',
  },
  {
    title: 'Your Sales Team',
    desc: 'We go to the field, find clients,\npresent proposals and close contracts.\nYou just need to execute the work.',
  },
  {
    title: 'Your Growth Engine',
    desc: 'From first contact to contract signing,\nwe handle the entire commercial process\nfor you.',
  },
];

const stats = [
  { value: '$30M+', label: 'REVENUE GENERATED' },
  { value: '400+', label: 'DEALS CLOSED' },
  { value: '02', label: 'STATES — FLORIDA & GEORGIA' },
  { value: '50', label: 'STATES COVERAGE — ACROSS AMERICA' },
];

export default function NotAgencySection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="not-agency"
      ref={ref}
      className="relative overflow-hidden"
      style={{ background: '#FFFFFF', padding: '100px 80px 0' }}
    >
      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Eyebrow */}
        <div
          className={`flex items-center justify-center gap-5 mb-10 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <div style={{ width: '40px', height: '1px', background: '#DC5014' }} />
          <p
            className="font-sans font-bold uppercase whitespace-nowrap"
            style={{ color: '#DC5014', fontSize: '11px', letterSpacing: '0.32em' }}
          >
            LET&apos;S BE CLEAR
          </p>
          <div style={{ width: '40px', height: '1px', background: '#DC5014' }} />
        </div>

        {/* Headline */}
        <div
          className={`text-center mb-6 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <h2 className="font-playfair mx-auto" style={{ fontSize: 'clamp(32px, 4vw, 56px)', lineHeight: 1.15 }}>
            <span style={{ color: '#0A0A0A', fontWeight: 400, display: 'block' }}>
              We Don&apos;t Run Ads.
            </span>
            <span
              className="italic"
              style={{ color: '#64B4B4', fontWeight: 400, display: 'block' }}
            >
              We Close Deals.
            </span>
          </h2>
        </div>

        {/* Sub */}
        <div
          className={`text-center mb-16 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <p
            className="font-sans mx-auto"
            style={{
              color: 'rgba(10,10,10,0.55)',
              fontSize: '16px',
              lineHeight: 1.8,
              maxWidth: '500px',
              fontWeight: 300,
            }}
          >
            Marketing and branding? Yes, we do that.
            But that&apos;s the commodity. What makes Barkfy
            different is what happens after:
            we become your commercial team.
          </p>
        </div>

        {/* 2-column grid */}
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ gap: '2px' }}
        >
          {/* LEFT — WHAT WE ARE NOT */}
          <div
            className="relative overflow-hidden"
            style={{
              background: '#F8F8F8',
              border: '1px solid #EBEBEB',
              padding: '52px 48px',
            }}
          >
            {/* Decorative × */}
            <span
              className="absolute font-sans select-none pointer-events-none"
              style={{
                right: '24px',
                top: '24px',
                fontSize: '120px',
                color: 'rgba(220,80,20,0.05)',
                lineHeight: 1,
                fontWeight: 700,
              }}
            >
              ×
            </span>

            {/* Header */}
            <div className="flex items-center gap-3 mb-8">
              <div
                className="flex items-center justify-center rounded-full flex-shrink-0"
                style={{
                  width: '32px',
                  height: '32px',
                  background: 'rgba(220,80,20,0.1)',
                }}
              >
                <span style={{ color: '#DC5014', fontSize: '16px', fontWeight: 700 }}>×</span>
              </div>
              <p
                className="font-sans font-bold uppercase"
                style={{ color: '#DC5014', fontSize: '11px', letterSpacing: '0.28em' }}
              >
                WHAT WE ARE NOT
              </p>
            </div>

            <ul className="flex flex-col gap-4">
              {notList.map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <div
                    className="flex items-center justify-center rounded-full flex-shrink-0"
                    style={{ width: '20px', height: '20px', background: 'rgba(220,80,20,0.08)' }}
                  >
                    <i className="ri-close-line" style={{ color: '#DC5014', fontSize: '12px' }} />
                  </div>
                  <span
                    className="font-sans"
                    style={{
                      color: '#999999',
                      fontSize: '15px',
                      textDecoration: 'line-through',
                      textDecorationColor: 'rgba(220,80,20,0.3)',
                    }}
                  >
                    {item}
                  </span>
                </li>
              ))}
            </ul>

            {/* Bottom note */}
            <div
              className="mt-10 pt-8"
              style={{ borderTop: '1px solid #EBEBEB' }}
            >
              <p
                className="font-sans italic"
                style={{ color: '#AAA', fontSize: '13px', lineHeight: 1.7 }}
              >
                &ldquo;We are not here to sell you a service.
                We are here to become part of your business.&rdquo;
              </p>
            </div>
          </div>

          {/* RIGHT — WHAT WE ARE */}
          <div
            className="relative overflow-hidden"
            style={{ background: '#143C3C', padding: '52px 48px' }}
          >
            {/* Decorative ✓ */}
            <span
              className="absolute font-sans select-none pointer-events-none"
              style={{
                right: '24px',
                top: '24px',
                fontSize: '120px',
                color: 'rgba(100,180,180,0.06)',
                lineHeight: 1,
                fontWeight: 700,
              }}
            >
              ✓
            </span>

            {/* Header */}
            <div className="flex items-center gap-3 mb-8">
              <div
                className="flex items-center justify-center rounded-full flex-shrink-0"
                style={{
                  width: '32px',
                  height: '32px',
                  background: 'rgba(100,180,180,0.15)',
                }}
              >
                <i className="ri-check-line" style={{ color: '#64B4B4', fontSize: '16px' }} />
              </div>
              <p
                className="font-sans font-bold uppercase"
                style={{ color: '#64B4B4', fontSize: '11px', letterSpacing: '0.28em' }}
              >
                WHAT WE ARE
              </p>
            </div>

            <ul className="flex flex-col gap-6">
              {weAreList.map((item) => (
                <li key={item.title} className="flex items-start gap-4">
                  <div
                    className="flex items-center justify-center rounded-full flex-shrink-0 mt-1"
                    style={{
                      width: '20px',
                      height: '20px',
                      background: 'rgba(100,180,180,0.15)',
                    }}
                  >
                    <i className="ri-check-line" style={{ color: '#64B4B4', fontSize: '11px' }} />
                  </div>
                  <div>
                    <p
                      className="font-sans font-semibold mb-1"
                      style={{ color: '#FFFFFF', fontSize: '14px' }}
                    >
                      {item.title}
                    </p>
                    <p
                      className="font-sans whitespace-pre-line"
                      style={{ color: 'rgba(255,255,255,0.55)', fontSize: '13px', lineHeight: 1.75, fontWeight: 300 }}
                    >
                      {item.desc}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Stats bar — full width, outside max-w container */}
      <div
        className={`mt-0 transition-all duration-700 delay-400 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        style={{
          background: '#F5F5F5',
          borderTop: '1px solid rgba(20,60,60,0.08)',
          padding: '48px 0',
          marginTop: '80px',
        }}
      >
        <div className="grid grid-cols-2 md:grid-cols-4">
          {stats.map((stat, i) => (
            <div
              key={stat.label}
              className="text-center"
              style={{
                padding: '0 40px',
                borderRight: i < stats.length - 1 ? '1px solid rgba(20,60,60,0.1)' : 'none',
              }}
            >
              <p
                className="font-playfair"
                style={{
                  color: '#DC5014',
                  fontSize: 'clamp(32px, 3.5vw, 52px)',
                  fontWeight: 400,
                  lineHeight: 1.0,
                  marginBottom: '8px',
                }}
              >
                {stat.value}
              </p>
              <p
                className="font-sans font-semibold uppercase"
                style={{
                  color: 'rgba(20,60,60,0.5)',
                  fontSize: '10px',
                  letterSpacing: '0.18em',
                  lineHeight: 1.5,
                }}
              >
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
